/** "Attach from file" picker — documents on the customer file / application (OneDrive · LoanDisk · Monday · DocuPilot · DocuSign)
 *  plus, when replying, the original email's own attachments. Multi-tick, search, 25 MB cap (over-cap rows greyed out). */
import { useEffect, useMemo, useState } from "react";
import { FileText, FolderOpen, Loader2, Mail, Search } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { MAX_TOTAL, fmtSize, pickItem } from "./attachments";

const ORIGIN_TONE = { OneDrive: "bg-sky-50 text-sky-800 border-sky-200", LoanDisk: "bg-emerald-50 text-emerald-800 border-emerald-200", Monday: "bg-violet-50 text-violet-800 border-violet-200",
  DocuPilot: "bg-amber-50 text-amber-800 border-amber-200", DocuSign: "bg-indigo-50 text-indigo-800 border-indigo-200", Email: "bg-slate-100 text-slate-700 border-slate-300" };
const fmtWhen = (s) => (s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");

function ScopeBar({ fileSource, scope, onScope }) {
  const [changing, setChanging] = useState(!scope);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const search = async () => {
    if (q.trim().length < 2) return;
    setBusy(true);
    try { const r = await fileSource.search(q.trim()); setRows(r); if (!r.length) toast.info(`Nothing found for “${q.trim()}”`); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1.5" data-testid="attach-scope">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <FolderOpen className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
        {scope ? <span className="font-medium text-indigo-900" data-testid="attach-scope-label">{fileSource.label(scope)}</span>
          : <span className="text-amber-800" data-testid="attach-scope-none">No {fileSource.noun} picked yet — search below</span>}
        <button type="button" className="text-[11px] text-indigo-700 hover:underline" onClick={() => setChanging((v) => !v)} data-testid="attach-scope-change">{changing ? "Keep this one" : "Change…"}</button>
      </div>
      {changing && (
        <div className="space-y-1">
          <div className="flex gap-1">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder={fileSource.placeholder} className="h-7 text-xs bg-white" data-testid="attach-scope-search" />
            <Button size="sm" variant="outline" className="h-7 px-2" disabled={busy || q.trim().length < 2} onClick={search} data-testid="attach-scope-search-btn">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}</Button>
          </div>
          {rows.length > 0 && (
            <div className="border rounded divide-y max-h-32 overflow-y-auto bg-white">
              {rows.map((r, i) => (
                <button key={i} type="button" className="w-full flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-slate-50 text-left" onClick={() => { onScope(r.scope); setChanging(false); setRows([]); }} data-testid={`attach-scope-opt-${i}`}>
                  <span className="font-mono font-semibold">{r.sf || "—"}</span><span className="truncate flex-1">{r.name}</span>{r.badge && <Badge variant="outline" className="text-[9px]">{r.badge}</Badge>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Row({ row, checked, disabled, onToggle, testId }) {
  return (
    <label className={cn("flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50", disabled && "opacity-50 cursor-not-allowed")} title={disabled ? "Would take the email over 25 MB" : row.name} data-testid={testId}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onToggle} data-testid={`${testId}-tick`} />
      <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className="truncate flex-1">{row.name}</span>
      {row.status && <span className="text-[10px] text-muted-foreground shrink-0">{row.status}</span>}
      <span className="text-[10px] text-muted-foreground shrink-0 w-20 text-right">{fmtWhen(row.last_modified)}</span>
      <span className="text-[10px] text-slate-500 shrink-0 w-14 text-right tabular-nums">{fmtSize(row.size)}</span>
    </label>
  );
}

export function AttachFromFileDialog({ fileSource, defaultScope, onScopeChange, messageAttachments, alreadyBytes = 0, existing = [], onAdd, onClose }) {
  const [scope, setScope] = useState(defaultScope || null);
  const [rows, setRows] = useState([]);
  const [mailRows, setMailRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(() => new Map());

  useEffect(() => {
    if (!scope) { setRows([]); return; }
    let alive = true;
    setLoading(true);
    fileSource.list(scope).then((r) => { if (alive) setRows(r.files || []); })
      .catch((e) => { if (alive) { setRows([]); toast.error(e.response?.data?.detail || e.message); } })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [scope, fileSource]);

  useEffect(() => {
    if (!messageAttachments) return;
    let alive = true;
    messageAttachments.list().then((r) => { if (alive) setMailRows((r.attachments || []).filter((a) => !a.is_zip).map((a) => ({ key: a.id, name: a.name, size: a.size, mime_type: a.contentType, origin: "Email" }))); }).catch(() => {});
    return () => { alive = false; };
  }, [messageAttachments]);

  const selBytes = [...sel.values()].reduce((s, it) => s + (it.size || 0), 0);
  const room = MAX_TOTAL - alreadyBytes - selBytes;
  const filter = (list) => (q.trim() ? list.filter((r) => `${r.name} ${r.origin}`.toLowerCase().includes(q.trim().toLowerCase())) : list);
  const groups = useMemo(() => {
    const g = new Map();
    filter(rows).forEach((r) => { if (!g.has(r.origin)) g.set(r.origin, []); g.get(r.origin).push(r); });
    return [...g.entries()];
  }, [rows, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (row, source, rowScope) => {
    const it = pickItem(row, source, rowScope);
    setSel((cur) => { const n = new Map(cur); if (n.has(it.id)) n.delete(it.id); else n.set(it.id, it); return n; });
  };
  const isOn = (row, source) => sel.has(`${source}:${row.key}`);
  const isTaken = (row, source) => existing.some((e) => e.id === `${source}:${row.key}`);
  const blocked = (row, source) => !isOn(row, source) && row.size != null && row.size > room;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="attach-from-file-dialog">
        <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><FolderOpen className="w-4 h-4 text-indigo-600" />Attach from file</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <ScopeBar fileSource={fileSource} scope={scope} onScope={(s) => { setScope(s); onScopeChange?.(s); }} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name or source…" className="h-7 text-xs" data-testid="attach-filter" />
          {messageAttachments && mailRows.length > 0 && (
            <div className="border rounded" data-testid="attach-group-email">
              <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 border-b text-[11px] font-medium"><Mail className="w-3 h-3" />From this email <span className="text-muted-foreground font-normal">{mailRows.length}</span></div>
              <div className="divide-y">{filter(mailRows).map((r, i) => <Row key={r.key} row={r} checked={isOn(r, "message_attachment")} disabled={isTaken(r, "message_attachment") || blocked(r, "message_attachment")} onToggle={() => toggle(r, "message_attachment", { message_id: messageAttachments.messageId })} testId={`attach-email-${i}`} />)}</div>
            </div>
          )}
          {loading ? <div className="py-6 flex justify-center text-xs text-muted-foreground gap-2"><Loader2 className="w-4 h-4 animate-spin" />Reading the file…</div>
            : scope && !rows.length ? <p className="text-xs text-muted-foreground py-3 text-center" data-testid="attach-empty">No documents on this {fileSource.noun} yet.</p>
            : groups.map(([origin, list]) => (
              <div key={origin} className="border rounded" data-testid={`attach-group-${origin}`}>
                <div className="flex items-center gap-2 px-2 py-1 bg-slate-50 border-b text-[11px] font-medium">
                  <Badge variant="outline" className={cn("text-[9px]", ORIGIN_TONE[origin] || "")}>{origin}</Badge><span className="text-muted-foreground font-normal">{list.length}</span>
                </div>
                <div className="divide-y max-h-56 overflow-y-auto">
                  {list.map((r, i) => <Row key={r.key} row={r} checked={isOn(r, fileSource.kind)} disabled={isTaken(r, fileSource.kind) || blocked(r, fileSource.kind)} onToggle={() => toggle(r, fileSource.kind, scope)} testId={`attach-row-${origin}-${i}`} />)}
                </div>
              </div>
            ))}
          <p className="text-[10px] text-muted-foreground">Files are pulled straight into the Outlook message on send — nothing downloads here. “size ?” files are checked against the 25 MB limit on send.</p>
        </div>
        <DialogFooter>
          <span className="text-[11px] text-muted-foreground mr-auto" data-testid="attach-selected-summary">{sel.size ? `${sel.size} selected · ${fmtSize(selBytes)} · ${fmtSize(Math.max(0, room))} left` : `${fmtSize(Math.max(0, MAX_TOTAL - alreadyBytes))} left of 25 MB`}</span>
          <Button variant="ghost" onClick={onClose} data-testid="attach-cancel">Cancel</Button>
          <Button disabled={!sel.size} onClick={() => { onAdd([...sel.values()]); onClose(); }} data-testid="attach-add">Add {sel.size || ""} {sel.size === 1 ? "file" : "files"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
